import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@pk/shared';
import type { HistoryCharacter, Id } from '@pk/shared';
import { usePedigree, useWorld } from '../api/hooks';
import { Badge, Empty, ErrorBox, Loading, PageHeader } from '../components/ui';
import { IconTree } from '../components/icons';
import { Heraldry } from '../components/Heraldry';
import '../styles/screens-people.css';

// Layout constants for the generational tree (px).
const BOX_W = 156;
const BOX_H = 58;
const SPOUSE_GAP = 8;
const COUPLE_GAP = 40;
const ROW_GAP = 72;

/** One rendered unit: a person alone or a spouse pair, drawn side by side at the same level. */
type Couple = { left: HistoryCharacter; right: HistoryCharacter | null; level: number };

/** level(node) = max(parents' levels) + 1; roots (no known parents) sit at level 0. */
function levelsOf(nodes: HistoryCharacter[]): Map<Id, number> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const levels = new Map<Id, number>();
  const inFlight = new Set<Id>();
  const level = (id: Id): number => {
    const cached = levels.get(id);
    if (cached !== undefined) return cached;
    if (!byId.has(id) || inFlight.has(id)) return -1; // missing parent or cycle guard
    inFlight.add(id);
    const n = byId.get(id)!;
    const parentLevels = [n.fatherId, n.motherId].filter((p): p is Id => !!p).map(level);
    const l = parentLevels.length ? Math.max(...parentLevels) + 1 : 0;
    inFlight.delete(id);
    levels.set(id, l);
    return l;
  };
  nodes.forEach((n) => level(n.id));
  return levels;
}

export function TreeScreen() {
  const { houseId } = useParams();
  const { t } = useTranslation();
  const pedigree = usePedigree(houseId);
  const world = useWorld();

  if (pedigree.isLoading) return <div className="tree-pad"><Loading /></div>;
  if (pedigree.isError || !pedigree.data) return <div className="tree-pad"><ErrorBox error={pedigree.error} onRetry={() => pedigree.refetch()} /></div>;
  const nodes = pedigree.data;
  const house = world.data?.houses.find((h) => h.id === houseId);
  if (nodes.length === 0) {
    return (
      <div className="tree-pad screen">
        <PageHeader icon={house ? <Heraldry seed={house.heraldrySeed} size={34} /> : <IconTree />} title={house ? t('tree.houseTitle', { name: house.name }) : t('tree.title')} />
        <Empty>{t('tree.empty')}</Empty>
      </div>
    );
  }
  const gens = Math.max(...Array.from(levelsOf(nodes).values())) + 1;
  return <TreeView nodes={nodes} houseId={houseId ?? null} house={house} gens={gens} />;
}

const MAX_K = 2.5;
const TOP_INSET = 64; // room for the floating header when fitting
const BOTTOM_INSET = 16;
const READABLE_K = 0.9; // node text stays >= ~11px
const SIDE_INSET = 16;
type View = { x: number; y: number; k: number };

function TreeView({ nodes, houseId, house, gens }: { nodes: HistoryCharacter[]; houseId: Id | null; house: { name: string; heraldrySeed: number; extinct?: boolean } | undefined; gens: number }) {
  const { t } = useTranslation();
  const couples = useMemo(() => layout(nodes), [nodes]);
  const width = Math.max(...couples.map((c) => c.x + c.w));
  const height = Math.max(...couples.map((c) => c.y)) + BOX_H;
  const head = nodes.find((n) => n.isHead);

  const wrapRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [view, setView] = useState<View | null>(null);
  const [legend, setLegend] = useState(false);
  const touched = useRef(false);
  const viewRef = useRef<View | null>(null);
  viewRef.current = view;

  // Never fit below a readable scale: if the whole tree does not fit, start on the head of house instead.
  const wholeK = useCallback((w: number, h: number) => Math.min(1, Math.max(40, w - SIDE_INSET * 2) / width, Math.max(40, h - TOP_INSET - BOTTOM_INSET) / height), [width, height]);
  const fitView = useCallback((w: number, h: number): View => {
    const whole = wholeK(w, h);
    if (whole >= READABLE_K) {
      const availH = h - TOP_INSET - BOTTOM_INSET;
      return { k: whole, x: (w - width * whole) / 2, y: TOP_INSET + (availH - height * whole) / 2 };
    }
    const k = READABLE_K;
    const hc = couples.find((q) => q.left.isHead || q.right?.isHead) ?? couples[0];
    const off = hc.right?.isHead ? BOX_W + SPOUSE_GAP : 0;
    const cx = hc.x + off + BOX_W / 2;
    const x = Math.min(SIDE_INSET, Math.max(w - SIDE_INSET - width * k, w / 2 - cx * k));
    return { k, x, y: TOP_INSET + 56 - hc.y * k };
  }, [wholeK, width, height, couples]);
  const minK = size.w ? Math.min(0.5, wholeK(size.w, size.h) * 0.7) : 0.1;

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fit on load and on resize until the person starts moving the view themselves.
  useLayoutEffect(() => {
    if (!size.w || !size.h) return;
    if (!touched.current || !viewRef.current) setView(fitView(size.w, size.h));
  }, [size, fitView]);

  const clamp = useCallback((v: View): View => {
    const k = Math.max(minK, Math.min(MAX_K, v.k));
    const pad = 80;
    const x = Math.min(size.w - pad, Math.max(pad - width * k, v.x));
    const y = Math.min(size.h - pad, Math.max(pad - height * k, v.y));
    return { k, x, y };
  }, [minK, size, width, height]);

  const zoomAt = useCallback((px: number, py: number, nk: number) => {
    const v = viewRef.current;
    if (!v) return;
    touched.current = true;
    const k = Math.max(minK, Math.min(MAX_K, nk));
    const wx = (px - v.x) / v.k;
    const wy = (py - v.y) / v.k;
    setView(clamp({ k, x: px - wx * k, y: py - wy * k }));
  }, [clamp, minK]);
  const zoomAtRef = useRef(zoomAt);
  zoomAtRef.current = zoomAt;

  // Wheel zoom needs a non-passive native listener to stop the page from scrolling.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const v = viewRef.current;
      if (v) zoomAtRef.current(e.clientX - r.left, e.clientY - r.top, v.k * Math.exp(-e.deltaY * 0.0018));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Pointer gestures: one pointer pans (after a movement threshold, so taps still open links), two pinch.
  const ptrs = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ sx: number; sy: number; v: View; moved: boolean; pinchD: number; pinchK: number } | null>(null);
  const dragged = useRef(false);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    dragged.current = false;
    const v = viewRef.current;
    if (!v) return;
    if (ptrs.current.size === 1) gesture.current = { sx: e.clientX, sy: e.clientY, v, moved: false, pinchD: 0, pinchK: v.k };
    else if (ptrs.current.size === 2 && gesture.current) {
      const [a, b] = Array.from(ptrs.current.values());
      gesture.current.pinchD = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      gesture.current.pinchK = v.k;
      gesture.current.moved = true;
      dragged.current = true;
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const p = ptrs.current.get(e.pointerId);
    const g = gesture.current;
    if (!p || !g) return;
    p.x = e.clientX; p.y = e.clientY;
    const r = stageRef.current!.getBoundingClientRect();
    if (ptrs.current.size >= 2) {
      const [a, b] = Array.from(ptrs.current.values());
      const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      zoomAt((a.x + b.x) / 2 - r.left, (a.y + b.y) / 2 - r.top, g.pinchK * (d / g.pinchD));
      return;
    }
    const dx = e.clientX - g.sx;
    const dy = e.clientY - g.sy;
    if (!g.moved) {
      if (Math.hypot(dx, dy) < 6) return;
      g.moved = true;
      dragged.current = true;
      try { stageRef.current?.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    }
    touched.current = true;
    setView(clamp({ k: g.v.k, x: g.v.x + dx, y: g.v.y + dy }));
  };
  const onPointerEnd = (e: React.PointerEvent) => {
    ptrs.current.delete(e.pointerId);
    if (ptrs.current.size === 0) gesture.current = null;
    else if (ptrs.current.size === 1 && gesture.current && viewRef.current) {
      // Pinch ended with one finger left: continue panning from here.
      const [rest] = Array.from(ptrs.current.values());
      gesture.current = { sx: rest.x, sy: rest.y, v: viewRef.current, moved: true, pinchD: 0, pinchK: viewRef.current.k };
    }
  };
  const swallowClick = (e: React.MouseEvent) => {
    if (dragged.current) { e.preventDefault(); e.stopPropagation(); dragged.current = false; }
  };

  const centerOn = (cx: number, cy: number, k: number) => {
    touched.current = true;
    const nk = Math.max(minK, Math.min(MAX_K, k));
    setView(clamp({ k: nk, x: size.w / 2 - cx * nk, y: (size.h + TOP_INSET) / 2 - cy * nk }));
  };
  const centerHead = () => {
    const c = head && couples.find((q) => q.left.id === head.id || q.right?.id === head.id);
    if (!c) return;
    const off = c.right && c.right.id === head!.id ? BOX_W + SPOUSE_GAP : 0;
    centerOn(c.x + off + BOX_W / 2, c.y + BOX_H / 2, Math.max(0.9, viewRef.current?.k ?? 1));
  };
  const onFocus = (e: React.FocusEvent) => {
    const el = e.target as HTMLElement;
    const v = viewRef.current;
    if (!v || !el.classList.contains('tree-node')) return;
    const l = v.x + el.offsetLeft * v.k;
    const tp = v.y + el.offsetTop * v.k;
    if (l < 0 || tp < TOP_INSET || l + BOX_W * v.k > size.w || tp + BOX_H * v.k > size.h) {
      centerOn(el.offsetLeft + BOX_W / 2, el.offsetTop + BOX_H / 2, v.k);
    }
  };

  // Elbow lines: from the bottom-centre of the parents' couple down to the top-centre of each child couple.
  const lines = useMemo(() => {
    const coupleOf = new Map<Id, PositionedCouple>();
    couples.forEach((c) => { coupleOf.set(c.left.id, c); if (c.right) coupleOf.set(c.right.id, c); });
    const out: string[] = [];
    for (const c of couples) {
      const childIds = new Set<Id>([...c.left.childIds, ...(c.right?.childIds ?? [])]);
      for (const cid of childIds) {
        const child = coupleOf.get(cid);
        if (!child || child === c) continue;
        const x1 = c.x + c.w / 2;
        const y1 = c.y + BOX_H;
        const x2 = child.x + child.w / 2;
        const y2 = child.y;
        const my = (y1 + y2) / 2;
        out.push(`M ${x1} ${y1} V ${my} H ${x2} V ${y2}`);
      }
    }
    return out;
  }, [couples]);

  const v = view ?? { x: 0, y: 0, k: 1 };
  const title = house ? t('tree.houseTitle', { name: house.name }) : t('tree.title');
  return (
    <div className="tree-wrap" ref={wrapRef}>
      <div
        className={`tree-stage ${dragged.current ? 'dragging' : ''}`}
        ref={stageRef}
        role="region"
        aria-label={t('tree.aria')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onClickCapture={swallowClick}
        onFocus={onFocus}
        onDragStart={(e) => e.preventDefault()}
      >
        <div className="tree-canvas" style={{ width, height, transform: `translate(${v.x}px, ${v.y}px) scale(${v.k})`, visibility: view ? 'visible' : 'hidden' }}>
          <svg className="tree-lines" width={width} height={height} aria-hidden="true">
            {lines.map((d, i) => <path key={i} d={d} />)}
          </svg>
          {couples.map((c) => (
            <Fragment key={c.key}>
              <NodeBox node={c.left} x={c.x} y={c.y} houseId={houseId} />
              {c.right && <NodeBox node={c.right} x={c.x + BOX_W + SPOUSE_GAP} y={c.y} houseId={houseId} />}
            </Fragment>
          ))}
        </div>
      </div>

      <div className="tree-head tree-ui">
        {head && <Link to={`/character/${head.id}`} className="tree-back" aria-label={t('tree.back')} title={t('tree.back')}><span aria-hidden="true">&lsaquo;</span></Link>}
        {house ? <Heraldry seed={house.heraldrySeed} size={30} /> : <IconTree />}
        <div className="tree-head-text">
          <h1>{title}</h1>
          <span className="tree-head-facts">
            {house?.extinct && <Badge tone="red">{t('tree.extinct')}</Badge>}
            <span>{t('tree.members', { count: nodes.length })}</span>
            {gens > 0 && <span>{t('tree.generations', { count: gens })}</span>}
          </span>
        </div>
      </div>

      <div className="tree-zoom tree-ui">
        <button type="button" aria-label={t('map.zoomIn')} title={t('map.zoomIn')} onClick={() => zoomAt(size.w / 2, size.h / 2, v.k * 1.4)}>+</button>
        <button type="button" aria-label={t('map.zoomOut')} title={t('map.zoomOut')} onClick={() => zoomAt(size.w / 2, size.h / 2, v.k / 1.4)}>&minus;</button>
        <button type="button" aria-label={t('map.fit')} title={t('map.fit')} onClick={() => { touched.current = false; setView(fitView(size.w, size.h)); }}>&#x2922;</button>
        {head && <button type="button" aria-label={t('tree.centerHead')} title={t('tree.centerHead')} onClick={centerHead}>&#9733;</button>}
      </div>

      <div className="tree-legend-wrap tree-ui">
        <button type="button" className={`tree-legend-btn ${legend ? 'on' : ''}`} aria-expanded={legend} onClick={() => setLegend((x) => !x)}>{t('ui.legend')}</button>
        {legend && (
          <div className="tree-legend-panel">
            <div className="tree-legend">
              <span><span className="sw house" aria-hidden="true" />{t('tree.legendHouse')}</span>
              <span><span className="sw other" aria-hidden="true" />{t('tree.legendOther')}</span>
              <span><span className="sw dead" aria-hidden="true" />{t('tree.legendDead')}</span>
              <span><span aria-hidden="true">&#9733;</span> {t('tree.legendHead')}</span>
            </div>
            <p className="tree-note">{t('tree.hint')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function NodeBox({ node, x, y, houseId }: { node: HistoryCharacter; x: number; y: number; houseId: Id | null }) {
  const { t } = useTranslation();
  const dead = node.deathDay !== null;
  return (
    <Link
      to={`/character/${node.id}`}
      className={`tree-node ${dead ? 'dead' : 'living'} ${houseId && node.houseId === houseId ? 'house' : ''}`}
      style={{ left: x, top: y, width: BOX_W, height: BOX_H }}
    >
      <span className="tree-name">
        {node.isHead && <span className="tree-state head" title={t('character.head')} aria-label={t('character.head')}>&#9733;</span>}
        {dead && <span className="tree-state" aria-label={t('tree.legendDead')}>&#10013;</span>}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.titledName ?? node.name}</span>
      </span>
      <span className="tree-dates">{formatDate(node.birthDay)} – {node.deathDay === null ? t('tree.present') : formatDate(node.deathDay)}</span>
    </Link>
  );
}

type PositionedCouple = Couple & { key: string; x: number; y: number; w: number };

/** Deterministic layout: one couple per row slot, couples grouped by computed level. */
function layout(nodes: HistoryCharacter[]): PositionedCouple[] {
  const levels = levelsOf(nodes);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const done = new Set<Id>();
  const couples: PositionedCouple[] = [];
  for (const n of nodes) {
    if (done.has(n.id)) continue;
    const spouse = n.spouseId ? byId.get(n.spouseId) : undefined;
    // Couples are the same generation by definition: use the max of the partners' levels, never
    // the level of whichever partner the iteration happened to reach first.
    const own = levels.get(n.id) ?? 0;
    const level = spouse ? Math.max(own, levels.get(spouse.id) ?? 0) : own;
    // Couples whose members are all outside the pedigree fall back to solo boxes.
    const key = spouse ? [n.id, spouse.id].sort().join('+') : n.id;
    const otherDone = spouse ? done.has(spouse.id) : false;
    if (otherDone) { done.add(n.id); continue; }
    const existing = couples.find((c) => c.key === key);
    if (existing) { done.add(n.id); continue; }
    const levelPeers = couples.filter((c) => c.level === level);
    const x = levelPeers.reduce((acc, c) => Math.max(acc, c.x + c.w), 0) + (levelPeers.length ? COUPLE_GAP : 0);
    const w = spouse ? BOX_W * 2 + SPOUSE_GAP : BOX_W;
    couples.push({ key, level, left: n, right: spouse ?? null, x, y: level * (BOX_H + ROW_GAP), w });
    done.add(n.id);
    if (spouse) done.add(spouse.id);
  }
  return couples;
}